// @flow
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { List } from 'immutable';
import { v4 as uuidv4 } from 'uuid';

import CircularProgress from '@material-ui/core/CircularProgress';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';

import { Formik, Form } from 'formik';
import type { FormikBag } from 'formik';

import InteractiveStateView from 'components/InteractiveStateView';
import QButton from 'components/QButton';

import {
  mkUpdateAccount,
  mkAccountAggregator,
} from 'data/accounts/types';
import { addInstitutionLoginAndAccounts } from 'data/institutionLogins/actions';
// import { getLogger } from 'utils/logger';
import tracker from 'utils/tracker';

import { getAccountDiscoveryData, getInstitutionLogin, getIsPolling, getIsSubmitting, getUpsertableAccountData } from '../selectors';

import AccountRow from './AccountRow';
import { useStyles } from './styles';
import {refreshAccounts, upsertAccounts} from '../../../../data/accounts/actions';

// const log = getLogger('InstitutionAccountsUpdater/index.js');

type Props = {
  trackingProperties: Object,

  onCancel: (any) => void,
  onReset: (any) => void,

  // When QCS supports ignore flag in general, can removee
  plaid: boolean,
};

const InstitutionAccountsUpdater = (props: Props) => {
  const { onCancel, onReset, plaid, trackingProperties } = props;
  const dispatch = useDispatch();

  const accountDiscoveryData = useSelector(getAccountDiscoveryData);
  const accounts = useSelector(getUpsertableAccountData);
  const isPolling = useSelector(getIsPolling);
  const isSubmitting = useSelector(getIsSubmitting);
  const institutionLogin = useSelector(getInstitutionLogin);

  const classes = useStyles();

  const handleCancelAndReset = (reset) => {
    tracker.track(tracker.events.addFIComplete, ({
      ...trackingProperties,
      accounts_added: 0,
      accounts_ignored: 0,
      accounts_linked: 0,
      accounts_changed_name: 0,
      accounts_changed_type: 0,
      accounts_changed_subtype: 0,
      accounts_already_connected: accountDiscoveryData.activeAccounts.length,
    }));
    if (reset) {
      if (onReset) {
        onReset();
      }
    } else if (onCancel) {
      onCancel();
    }
  };

  const handleSubmit = (values) => {
    let numberAdded = 0;
    let numberIgnored = 0;
    let numberLinked = 0;
    let numberChangedNames = 0;
    let numberChangedType = 0;
    let numberChangedSubType = 0;
    const accountsToUpsert = accounts.reduce((list, account) => {
      const accountValues = values[account.formFieldName];
      const accountAction = accountValues.action.type;
      const aggregatorParams = {
        cpId: account.discoveredAccount.id,
        channel: account.discoveredAccount.channel,

        accountName: account.discoveredAccount.name,
        accountNumberMasked: account.discoveredAccount.maskedNumber,
      };
      const aggregators = List([mkAccountAggregator(aggregatorParams)]);
      const accountParams = {
        id: accountValues.action.accountToLinkTo ? accountValues.action.accountToLinkTo.id : null,
        clientId: uuidv4().toUpperCase(),
        institutionLoginId: institutionLogin.id,
        isIgnored: accountAction === 'IGNORE',

        name: accountValues.name,
        subType: accountValues.type.qcsSubType,
      };
      let accountToUpsert = null;
      if (accountAction !== 'IGNORE' || plaid) {
        accountToUpsert = mkUpdateAccount(accountValues.type.qcsType, { ...accountParams, aggregators });
        list.push(accountToUpsert);
      }
      switch (accountAction) {
        case 'ADD_ACCOUNT':
          numberAdded += 1;
          if (accountToUpsert) {
            numberChangedNames = (account.name !== accountToUpsert.name) ? 1 : 0;
            numberChangedType += (account.type !== accountToUpsert.type) ? 1 :0;
            numberChangedSubType += (account.subType !== accountToUpsert.subType) ? 1 :0;
          }
          break;
        case 'IGNORED':
          numberIgnored += 1;
          break;
        case 'LINK_TO':
          numberLinked += 1;
          break;
        default:
          break;
      }
      return list;
    }, []);

    if (institutionLogin.id && institutionLogin.id !== '0') {
      const metadata = {afterSuccessAction: refreshAccounts({institutionLoginId: institutionLogin.id}, { context: 'addAccounts' })};
      dispatch(upsertAccounts(accountsToUpsert, metadata));
    }
    else {
      dispatch(addInstitutionLoginAndAccounts({ institutionLogin, accounts: accountsToUpsert }));
    }
    tracker.track(tracker.events.addFIComplete, {
      ...trackingProperties,
      accounts_added: numberAdded,
      accounts_ignored: numberIgnored,
      accounts_linked: numberLinked,
      accounts_changed_name: numberChangedNames,
      accounts_changed_type: numberChangedType,
      accounts_changed_subtype: numberChangedSubType,
      accounts_already_connected: accountDiscoveryData.activeAccounts.length,
    });
  };

  const renderForm = (formikBag: FormikBag) => {
    const {
      handleSubmit: formikHandleSubmit,
      values,
    } = formikBag;

    let linkError = null;
    const stagedLinkAccounts = [];
    values && Object.values(values).forEach((account) => {
      const { action } = account;
      if (action.accountToLinkTo) {
        const { id } = action.accountToLinkTo;
        if (stagedLinkAccounts.includes(id)) {
          linkError = 'You cannot link multiple accounts to the same Unconnected Account';
        } else {
          stagedLinkAccounts.push(id);
        }
      }
    });

    // This is to make sure two accountNames aren't the same
    const stagedNameList = Object.values(values).filter((value) => value.action.type === 'ADD_ACCOUNT').map((value) => value.name.toLowerCase());

    return (
      <Form className={classes.form} onSubmit={formikHandleSubmit}>
        {accounts.length > 0 ?
          <>
            <DialogContent classes={{ root: classes.dialogContentRoot }}>
              <div className={classes.linkOverload}>
                {linkError}
              </div>
              <div className={classes.tableWrapper}>
                <Table className={classes.table}>
                  <TableBody>
                    {accounts.map((account, index) => (
                      <AccountRow
                        account={account}
                        formik={formikBag}
                        index={index}
                        key={account.formFieldName}
                        otherNames={stagedNameList}
                        values={values[account.formFieldName]}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            </DialogContent>
            <DialogActions className={classes.buttons}>
              <QButton
                id="acct-upsert-cancel"
                variant="outlined"
                width={140}
                onClick={onCancel}
                style={{ marginRight: 12 }}
              >
                Cancel
              </QButton>

              <QButton
                id="acct-upsert-submit"
                type="submit"
                variant="contained"
                width={140}
                disabled={isSubmitting || Boolean(linkError)}
              >
                Complete
              </QButton>
            </DialogActions>
          </>
          :
          <InteractiveStateView
            primary=" "
            secondary="You have already added all your accounts at this bank."
            goText="TRY ANOTHER BANK"
            backText="CANCEL"
            goClick={() => handleCancelAndReset(true)}
            backClick={() => handleCancelAndReset(false)}
          />
        }
      </Form>
    );
  };

  if (isPolling || isSubmitting) {
    return (
      <div className={classes.loadingRoot}>
        <CircularProgress size={130} className={classes.progress}/>
      </div>
    );
  }

  const accountsValues = accounts.reduce((obj, account) => {
    // eslint-disable-next-line no-param-reassign
    obj[account.formFieldName] = { name: account.name, type: account.type, action: account.action };
    return obj;
  }, {});

  return (
    <Formik
      initialValues={{ ...accountsValues }}
      onSubmit={handleSubmit}
      render={renderForm}
      validateOnChange
      validateOnSubmit
    />
  );
};

export default InstitutionAccountsUpdater;

